import React, { useEffect, useMemo, useState } from 'react';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { api } from '@/lib/api';
import KindergartenView from '../journal/KindergartenView';

/**
 * Страница «Посещаемость» — только для детсадовских групп. Сверху селекторы
 * учебного года и группы, ниже — KindergartenView с матрицей посещаемости.
 *
 * Школа на этой странице не показывается (для неё есть «Электронный дневник»).
 */
const URL_SCHEMA = { year_id: '', class_id: '' };

function isoToday() { return new Date().toISOString().slice(0, 10); }

export default function AttendancePage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [classes, setClasses]     = useState([]);
  const [years, setYears]         = useState([]);
  const [refsReady, setRefsReady] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/classes'),
      api.get('/api/years_study').catch(() => ({ rows: [] })),
    ]).then(([c, y]) => {
      // В дропдауне только детсадовские группы.
      const groups = (c.rows || []).filter((x) => x.stage === 'kindergarten');
      setClasses(groups);
      const yearsList = (y.rows || []).filter((x) => !x.archive);
      setYears(yearsList);

      const today = isoToday();
      const patch = {};

      let yearId = params.year_id ? Number(params.year_id) : null;
      if (!yearId || !yearsList.some((yr) => yr.id === yearId)) {
        const current = yearsList.find((yr) => yr.dateStart <= today && today <= yr.dateEnd);
        const fallback = current || yearsList[0];
        if (fallback) { yearId = fallback.id; patch.year_id = String(fallback.id); }
      }

      const inYear = yearId ? groups.filter((x) => x.yearId === yearId) : groups;
      const cid = params.class_id ? Number(params.class_id) : 0;
      const stillValid = cid && inYear.some((x) => x.id === cid);
      if (!stillValid && inYear.length) patch.class_id = String(inYear[0].id);
      else if (!stillValid && !inYear.length && params.class_id) patch.class_id = '';

      if (Object.keys(patch).length) setParams(patch);
      setRefsReady(true);
    }).catch(() => { setRefsReady(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupsInYear = useMemo(() => {
    if (!params.year_id) return classes;
    const yid = Number(params.year_id);
    return classes.filter((c) => c.yearId === yid);
  }, [classes, params.year_id]);

  const onYearChange = (e) => {
    const yid = e.target.value;
    const yidNum = Number(yid);
    const inYear = classes.filter((c) => c.yearId === yidNum);
    const patch = { year_id: yid };
    const cid = params.class_id ? Number(params.class_id) : 0;
    if (!cid || !inYear.some((c) => c.id === cid)) {
      patch.class_id = inYear.length ? String(inYear[0].id) : '';
    }
    setParams(patch);
  };

  const breadcrumb = useMemo(() => {
    const arr = ['Обучение'];
    const cls = params.class_id ? classes.find((c) => c.id === Number(params.class_id)) : null;
    if (cls) arr.push({ label: 'Классы', to: '/classes' });
    if (cls) arr.push({ label: cls.code, to: '/classes/' + cls.id });
    arr.push('Посещаемость');
    return arr;
  }, [classes, params.class_id]);

  return (
    <>
      <Topbar
        breadcrumb={breadcrumb}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="jr-pickers">
        <select
          value={params.year_id || ''}
          onChange={onYearChange}
          className="jr-pickers__class"
        >
          <option value="">— учебный год —</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>{y.name}</option>
          ))}
        </select>

        <select
          value={params.class_id || ''}
          onChange={(e) => setParams({ class_id: e.target.value })}
          className="jr-pickers__class"
          disabled={!params.year_id || groupsInYear.length === 0}
        >
          <option value="">— группа —</option>
          {groupsInYear.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code}{c.name && c.name !== c.code ? ' · ' + c.name : ''}
            </option>
          ))}
        </select>
      </div>

      {!refsReady ? (
        <div className="ref-empty">Загрузка…</div>
      ) : groupsInYear.length === 0 ? (
        <div className="ref-empty">В этом учебном году нет детсадовских групп.</div>
      ) : !params.class_id ? (
        <div className="ref-empty">Выберите группу.</div>
      ) : (
        <KindergartenView classId={Number(params.class_id)} />
      )}
    </>
  );
}
